import { Duration } from 'luxon';
import { ComponentProps } from 'react';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { Inquiry, InquiryBehaviorThreatLevel } from 'dashboard/models/inquiry';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Heading } from 'design-system/components/Text';
import { HOURS_ELAPSED_FORMAT, MINUTES_SECONDS_ELAPSED_FORMAT } from 'lib/constants/luxonFormats';
import { assertNever } from 'lib/utils/types';

interface InquiryBehaviorProps {
  inquiry: Inquiry;
}

const InquiryBehavior = ({ inquiry }: InquiryBehaviorProps) => {
  const {
    'request-spoof-attempts': requestSpoofAttempts,
    'user-agent-spoof-attempts': userAgentSpoofAttempts,
    'distraction-events': distractionEvents,
    'shortcut-copies': shortcutCopies,
    'shortcut-pastes': shortcutPastes,
    'autofill-starts': autofillStarts,
    'completion-time': completionTime,

    'hesitation-percentage': hesitationPercentage,
    'behavior-threat-level': behaviorThreatLevel,
  } = inquiry.attributes;

  if (inquiry.relationships.sessions.data.length > 0) {
    let threatLevelStickerVariant: ComponentProps<typeof Sticker>['variant'];

    switch (behaviorThreatLevel) {
      case InquiryBehaviorThreatLevel.Low:
        threatLevelStickerVariant = 'success';
        break;
      case InquiryBehaviorThreatLevel.Medium:
        threatLevelStickerVariant = 'caution';
        break;
      case InquiryBehaviorThreatLevel.High:
        threatLevelStickerVariant = 'danger';
        break;
      case undefined:
      case null:
        threatLevelStickerVariant = 'caution';
        break;
      default:
        assertNever(behaviorThreatLevel);
    }

    let completionTimeFormatted;
    if (completionTime) {
      completionTimeFormatted =
        completionTime >= 60 * 60
          ? Duration.fromObject({ seconds: completionTime }).toFormat(HOURS_ELAPSED_FORMAT)
          : Duration.fromObject({ seconds: completionTime }).toFormat(
              MINUTES_SECONDS_ELAPSED_FORMAT,
            );
    }

    return (
      <Card>
        <Card.Body>
          <Card.Title>
            Behavior{' '}
            <HelpIcon>
              In addition to data collected from end users, Persona automatically collects and
              identifies additional behavioral data to help inform your understanding of risk and
              fraud.
            </HelpIcon>
          </Card.Title>
          <Spacer height={1.5} width={2.5} />
          <AttributeGrid>
            <AttributeItem
              title={
                <>
                  Behavior threat level{' '}
                  <HelpIcon>
                    A predictive, proprietary score you can use to assess fraud risk. This score is
                    created by Persona and based on a combination of behavioral signals.
                  </HelpIcon>
                </>
              }
              value={
                behaviorThreatLevel && (
                  <Sticker variant={threatLevelStickerVariant}>{behaviorThreatLevel}</Sticker>
                )
              }
            />
            <AttributeItem
              title={
                <>
                  User completion time <HelpIcon>Time duration of user interaction</HelpIcon>
                </>
              }
              value={completionTimeFormatted}
            />
            <AttributeItem
              title={
                <>
                  Distraction events <HelpIcon>Number of times the user left the flow</HelpIcon>
                </>
              }
              value={distractionEvents?.toString()}
            />
            <AttributeItem
              title={
                <>
                  Request spoof attempts{' '}
                  <HelpIcon>Number of requests that were likely spoofed</HelpIcon>
                </>
              }
              value={requestSpoofAttempts?.toString()}
            />
            <AttributeItem
              title={
                <>
                  User agent spoof attempts{' '}
                  <HelpIcon>
                    Number of user agent headers that were likely spoofed on seen devices
                  </HelpIcon>
                </>
              }
              value={userAgentSpoofAttempts?.toString()}
            />
            <AttributeItem
              title={
                <>
                  Hesitation percentage{' '}
                  <HelpIcon>Percentage of time in the flow where the user made no inputs</HelpIcon>
                </>
              }
              value={hesitationPercentage && `${hesitationPercentage?.toFixed(3)}%`}
            />
            <AttributeItem
              title={
                <>
                  Shortcut usage (copies){' '}
                  <HelpIcon>
                    Number of times the user used a keyboard shortcut to copy information
                  </HelpIcon>
                </>
              }
              value={shortcutCopies?.toString()}
            />
            <AttributeItem
              title={
                <>
                  Shortcut usage (pastes){' '}
                  <HelpIcon>
                    Number of times the user used a keyboard shortcut to paste information
                  </HelpIcon>
                </>
              }
              value={shortcutPastes}
            />
            <AttributeItem
              title={
                <>
                  Autofill starts{' '}
                  <HelpIcon>Number of times the user autofilled a form field</HelpIcon>
                </>
              }
              value={autofillStarts?.toString()}
            />
            {/*
              // TODO: Add this back if we can find a calculation method reliable cross-browser
              <AttributeItem
              title={
                <>
                  Autofill cancels{' '}
                  <HelpIcon>Number of times the user cleared an autofilled form field</HelpIcon>
                </>
              }
              value={autofillCancels?.toString()}
            /> */}
          </AttributeGrid>
        </Card.Body>
      </Card>
    );
  } else {
    return (
      <Card>
        <Card.Body>
          <Card.Title>Behavior</Card.Title>
          <Spacer height={1.5} width={2.5} />
          <TableNullState dataTestBlock='inquiry-behavior-card'>
            <Heading size='sm' data-test='inquiry-behavior-card__missing'>
              Behavior is only calculated for inquiries with sessions.
            </Heading>
          </TableNullState>
        </Card.Body>
      </Card>
    );
  }
};

export default InquiryBehavior;
